import { useEffect, useRef, useState } from "react";

// Adapted from React Bits' <DecryptedText />: characters scramble, then lock in left-to-right.

const DEFAULT_CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%&*<>/\\";

interface DecryptedTextProps {
  text: string;
  speed?: number;
  startDelay?: number;
  characters?: string;
  className?: string;
  style?: React.CSSProperties;
  onComplete?: () => void;
}

const DecryptedText = ({
  text,
  speed = 40,
  startDelay = 0,
  characters = DEFAULT_CHARS,
  className,
  style,
  onComplete,
}: DecryptedTextProps) => {
  const [display, setDisplay] = useState(() =>
    text.replace(/\S/g, () => characters[0])
  );
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    let revealed = 0;
    let interval: ReturnType<typeof setInterval>;
    const scramble = () =>
      text
        .split("")
        .map((char, i) => {
          if (char === " " || i < revealed) return char;
          return characters[Math.floor(Math.random() * characters.length)];
        })
        .join("");

    const timeout = setTimeout(() => {
      let tick = 0;
      interval = setInterval(() => {
        tick++;
        // lock in one character every other frame so the scramble is visible
        if (tick % 2 === 0) revealed++;
        setDisplay(scramble());
        if (revealed >= text.length) {
          clearInterval(interval);
          setDisplay(text);
          onCompleteRef.current?.();
        }
      }, speed);
    }, startDelay);

    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, [text, speed, startDelay, characters]);

  return (
    <span className={className} style={style} aria-label={text}>
      <span aria-hidden>{display}</span>
    </span>
  );
};

export default DecryptedText;
